/** @jsxImportSource @emotion/react */
import { Form } from 'react-final-form'

import { Button } from '@mui/material'

import { useMutation, useQuery } from '@apollo/client'
import { FC } from 'react'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import { FindAllStudyToolsDescription } from '__generatedTypes__/FindAllStudyToolsDescription'
import { CREATE_OR_UPDATE_DESCRIPTIONS, FIND_ALL_DESCRIPTIONS } from 'queries/description'
import {
  CreateOrUpdateStudyToolsDescription,
  CreateOrUpdateStudyToolsDescriptionVariables,
} from '__generatedTypes__/CreateOrUpdateStudyToolsDescription'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { toast } from 'utils/toast'
import * as classes from './styles'

type FormValues = {
  description: string
}

type Props = {
  title?: string
  page: string
}

type PropsInner = Props & {
  platform: Platform
}

export const DescriptionForm: FC<Props> = withPlatformCheck(({ page, title, platform }: PropsInner) => {
  const descriptionVariables = {
    query: {
      where: {
        page,
        platform: { id: platform.id },
      },
    },
  }
  const { data, loading, refetch } = useQuery<FindAllStudyToolsDescription>(FIND_ALL_DESCRIPTIONS, {
    variables: descriptionVariables,
  })

  const [createOrUpdateDescription, { loading: updateLoading }] = useMutation<
    CreateOrUpdateStudyToolsDescription,
    CreateOrUpdateStudyToolsDescriptionVariables
  >(CREATE_OR_UPDATE_DESCRIPTIONS)

  const descriptionItem = data?.findAllStudyToolsDescription?.items[0]
  const initialValues = descriptionItem?.description ? { description: descriptionItem.description } : {}

  const handleSubmit = async (values: FormValues) => {
    try {
      await createOrUpdateDescription({
        variables: {
          input: {
            id: descriptionItem?.id,
            page,
            platform: { id: platform.id },
            description: values.description || '',
          },
        },
      })
      await refetch()
      toast.success('Description saved successfully')
    } catch (error) {
      toast.error('Failed to save description')
    }
  }

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, dirty }) => (
        <form onSubmit={handleSubmit} css={classes.form}>
          <TextareaAutosize name="description" label={title ?? 'Description'} css={classes.textarea} />

          <Button color="blue" type="submit" size="large" disabled={!dirty || loading || updateLoading}>
            Save Edits
          </Button>
        </form>
      )}
    />
  )
})
